import { useState, useLayoutEffect } from "react";

export const BREAKPOINT_MD = 768;
export const BREAKPOINT_LG = 1024;

type Breakpoint = "base" | "md" | "lg";
type BreakpointValues<T> = Partial<Record<Breakpoint, T>> & { base: T };

export const useIsMobile = () => {
  return useResponsiveValue({ base: true, md: false });
};

export const useResponsiveValue = <T>(values: BreakpointValues<T>) => {
  const [currentValue, setCurrentValue] = useState<T>(values.base);

  useLayoutEffect(() => {
    function onBaseChange(event: MediaQueryListEvent) {
      if (event.matches) {
        setCurrentValue(getValue(values, "base"));
      }
    }

    function onMdChange(event: MediaQueryListEvent) {
      if (event.matches) {
        setCurrentValue(getValue(values, "md"));
      }
    }

    function onLgChange(event: MediaQueryListEvent) {
      if (event.matches) {
        setCurrentValue(getValue(values, "lg"));
      }
    }

    const baseMql = matchMedia(`(max-width: ${BREAKPOINT_MD - 1}px)`);
    const mdMql = matchMedia(
      `(min-width: ${BREAKPOINT_MD}px) and (max-width: ${BREAKPOINT_LG - 1}px)`,
    );
    const lgMql = matchMedia(`(min-width: ${BREAKPOINT_LG}px)`);

    baseMql.addEventListener("change", onBaseChange);
    mdMql.addEventListener("change", onMdChange);
    lgMql.addEventListener("change", onLgChange);

    // set initial value
    setCurrentValue(
      getValue(values, lgMql.matches ? "lg" : mdMql.matches ? "md" : "base"),
    );

    return () => {
      baseMql.removeEventListener("change", onBaseChange);
      mdMql.removeEventListener("change", onMdChange);
      lgMql.removeEventListener("change", onLgChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return currentValue;
};

function getValue<T>(values: BreakpointValues<T>, breakpoint: Breakpoint) {
  // mobile-firstな考え方で、lgがなければmd、mdがなければbaseを返す
  switch (breakpoint) {
    case "md":
      return values.md ?? values.base;
    case "lg":
      return values.lg ?? values.md ?? values.base;
    default:
      return values.base;
  }
}
